import React, { useEffect, useMemo, useRef, useState } from "react";

import { isEmpty } from "ramda";

import archivalDataApi from "apis/archivalData";
import { Toastr, PageLoader } from "clearsense-ui/components";
import { useColumnHeaderSelector } from "hooks/useColumnHeaderSelector";
import useDebounce from "hooks/useDebounce";

import ActionHeader from "./ActionHeader";
import { PENDING_DOCUMENT_STATUS } from "./constants";
import DocumentsTable from "./DocumentsTable";
import { getColumns } from "./DocumentsTable/data";
import PdfModal from "./PdfModal";
import PrimaryHeader from "./PrimaryHeader";
import UploadPane from "./UploadPane";

const ArchivalReports = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [result, setResult] = useState([]);
  const [documents, setDocuments] = useState();
  const [isUploadFile, setIsUploadFile] = useState(false);
  const [search, setSearch] = useState("");
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
  const [selectedPdfUrl, setSelectedPdfUrl] = useState();

  const polling = useRef(null);
  const debouncedSearchString = useDebounce(search, 1000);

  const fetchUpdatedDocuments = async () => {
    try {
      const { data } = await archivalDataApi.list();
      const pending = data.documents.filter(doc =>
        PENDING_DOCUMENT_STATUS.includes(doc.status)
      );
      if (pending.length === 0) {
        setDocuments(data.documents);
        clearInterval(polling.current);
        polling.current = null;
      }
    } catch (error) {
      logger.error(error);
    }
  };

  const fetchDocuments = async () => {
    setIsLoading(true);
    try {
      const { data } = await archivalDataApi.list();
      setDocuments(data.documents);
      setResult(data.documents);
      if (
        data.documents.filter(doc =>
          PENDING_DOCUMENT_STATUS.includes(doc.status)
        ).length
      ) {
        polling.current = setInterval(fetchUpdatedDocuments, 5000);
      }
    } catch (error) {
      logger.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async term => {
    setIsTableLoading(true);
    try {
      const { data } = await archivalDataApi.search(term);
      setResult(data.matches);
    } catch (err) {
      Toastr.error("Something went wrong.");
    } finally {
      setIsTableLoading(false);
    }
  };

  const handleViewPdf = async url => {
    setIsPdfLoading(true);
    setIsPdfModalOpen(true);
    try {
      const { data } = await archivalDataApi.viewPdf({ file_url: url });
      const blob = new Blob([data]);
      const fileUrl = URL.createObjectURL(blob.slice(0, blob.size, data.type));
      setSelectedPdfUrl(fileUrl);
    } catch {
      setIsPdfModalOpen(false);
    } finally {
      setIsPdfLoading(false);
    }
  };

  const allTableColumns = useMemo(() => getColumns(handleViewPdf), []);
  const { columns, toggleVisibility } =
    useColumnHeaderSelector(allTableColumns);

  useEffect(() => {
    fetchDocuments();
    return () => {
      if (polling.current) clearInterval(polling.current);
    };
  }, []);

  useEffect(() => {
    if (debouncedSearchString.length <= 2) setResult(documents);
    else handleSearch(debouncedSearchString);
  }, [debouncedSearchString, documents]);

  if (isLoading) {
    return (
      <div className="h-full">
        <PageLoader />
      </div>
    );
  }

  return (
    <div className="cs-ui-container cs-ui-container__table">
      <PrimaryHeader openUploadPane={() => setIsUploadFile(true)} />
      {(result?.length > 0 || !isEmpty(debouncedSearchString)) && (
        <ActionHeader
          result={result}
          search={search}
          setSearch={setSearch}
          columns={columns}
          toggleVisibility={toggleVisibility}
        />
      )}
      <DocumentsTable
        columns={columns.selected}
        rows={result}
        isLoading={isTableLoading}
        openUploadPane={() => setIsUploadFile(true)}
        debouncedSearchString={debouncedSearchString}
      />
      <UploadPane
        isOpen={isUploadFile}
        onClose={() => setIsUploadFile(false)}
      />
      <PdfModal
        isOpen={isPdfModalOpen}
        onClose={() => {
          setIsPdfModalOpen(false);
          setSelectedPdfUrl();
        }}
        isLoading={isPdfLoading}
        url={selectedPdfUrl}
      />
    </div>
  );
};

export default ArchivalReports;
